"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { garagemAuth } from "@/db/auth/client";
import { toast } from "@garagem/ui/components/sonner";
import { authQueryKeys } from "./query-keys";
import { useOrganization } from "./organization";
import { useTeams } from "./teams";
import { getDefaultTeam } from "./utils";
import { getUserFriendlyErrorMessage } from "./parse-auth-error";
import { useTRPC } from "@/lib/trpc/client";
import { useAuth } from "@/components/providers/auth-provider";

// Invitation hooks
// Uses our custom tRPC endpoint instead of Better Auth's listInvitations
// to avoid Better Auth's internal filtering of expired invitations
export function useInvitations(options?: { enabled?: boolean }) {
  const trpc = useTRPC();
  return trpc.auth.listOrganizationInvitations.useQuery(undefined, {
    enabled: options?.enabled ?? true,
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();
  const { data: organization } = useOrganization();
  const { data: teams = [] } = useTeams();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      role: "owner" | "admin" | "member"; // Better Auth role mapping
      originalRole?: string; // Original role (for custom roles - UUID or built-in like "gerente")
      teamId?: string;
      resend?: boolean;
      termTemplateIds?: string[];
    }) => {
      // Get the default team to ensure user is always added to it
      const defaultTeam = getDefaultTeam(teams, organization?.name);
      
      // If no specific team is provided, use the default team
      let targetTeamId = data.teamId;
      if (!targetTeamId && defaultTeam) {
        targetTeamId = defaultTeam.id;
      }

      // Use our custom endpoint which supports ADMIN_ROLES (includes gerente)
      const response = await garagemAuth.organization.sendInvitation({
        email: data.email,
        role: data.originalRole || data.role,
        teamId: targetTeamId,
        resend: data.resend,
        termTemplateIds: data.termTemplateIds,
      });
      
      if (response.error) throw response.error;
      
      // If a specific team was requested AND it's not the default team,
      // we need to send a second invitation to add them to the default team as well
      if (data.teamId && defaultTeam && data.teamId !== defaultTeam.id) {
        try {
          const defaultTeamResponse = await garagemAuth.organization.sendInvitation({
            email: data.email,
            role: data.originalRole || data.role,
            teamId: defaultTeam.id,
            resend: false,
          });
          
          if (defaultTeamResponse.error) {
            console.warn("Failed to add user to default team:", defaultTeamResponse.error);
            // Don't throw - the main invitation succeeded
          }
        } catch (error) {
          console.warn("Failed to add user to default team:", error);
          // Don't throw - the main invitation succeeded
        }
      }
      
      return response.data;
    },
    onSuccess: (data, variables) => {
      const inviteResult = data as { managedDomain?: boolean } | undefined;
      if (!inviteResult?.managedDomain) {
        toast.success(
          variables.resend ? "Convite reenviado!" : "Convite enviado com sucesso!"
        );
      }
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.invitations(),
      });
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.details(),
      });
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error) => {
      const errorMessage = getUserFriendlyErrorMessage(error);
      toast.error(errorMessage.title, {
        description: errorMessage.description,
      });
    },
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) => {
      const response = await garagemAuth.organization.cancelInvitation({
        invitationId,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Convite cancelado");
      // Invalidate all organization-related queries to refresh the membros page
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.invitations(),
      });
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.details(),
      });
      // Also invalidate tRPC queries for teams with members
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error) => {
      const errorMessage = getUserFriendlyErrorMessage(error);
      toast.error(errorMessage.title, {
        description: errorMessage.description,
      });
    },
  });
}

/**
 * Get pending invitations for the current user's email
 * Returns invitations sent to the authenticated user, excluding orgs where user is already a member
 */
export function useMyPendingInvitations(options?: { skip?: boolean }) {
  const trpc = useTRPC();
  const { session, isLoading: isAuthLoading } = useAuth();
  
  // Skip if explicitly requested (user has active org in layout)
  const shouldSkip = options?.skip ?? false;
  
  // Enable query if we have a session with user (even if still loading, as long as user exists)
  // This ensures the query runs as soon as the user is available
  const isEnabled = !shouldSkip && !!session?.user && (!!session.user.email || !isAuthLoading);
  
  const query = trpc.auth.getMyPendingInvitations.useQuery(undefined, {
    enabled: isEnabled,
    retry: false, // Don't retry on auth errors
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Return empty array immediately if skipped
  if (shouldSkip) {
    return { ...query, data: [], isLoading: false };
  }

  return query;
}